/**
 * Phase 126.B — Document meta hook.
 *
 * Sets `document.title` plus a stable set of OG / Twitter meta tags on
 * mount so public pages preview well when teachers paste them into
 * Twitter, Facebook, Bluesky, Slack, iMessage, etc. Tags are created on
 * demand and removed on unmount so we don't leak state across SPA
 * navigations.
 *
 * Scraper visibility (verified Aug 2026 against live prod): the Emergent
 * deploy prerenders each route server-side, so the tags this hook injects
 * ARE baked into the HTML that non-JS crawlers (facebookexternalhit,
 * Twitterbot, Slack, etc.) fetch — confirmed via
 * `curl -A facebookexternalhit https://atomicedu.ca/free-lesson-generator`.
 * The old "FB won't see client-side tags" caveat does NOT apply on this
 * platform. NOTE: this prerender only runs on the deployed site, not the
 * preview dev server — verify OG changes with the Facebook Sharing
 * Debugger against the LIVE URLs after each deploy, never a browser check.
 */
import { useEffect } from "react";

const TAGS = [
  ["meta[property='og:title']", "property", "og:title"],
  ["meta[property='og:description']", "property", "og:description"],
  ["meta[property='og:type']", "property", "og:type"],
  ["meta[property='og:url']", "property", "og:url"],
  ["meta[property='og:image']", "property", "og:image"],
  ["meta[name='twitter:card']", "name", "twitter:card"],
  ["meta[name='twitter:title']", "name", "twitter:title"],
  ["meta[name='twitter:description']", "name", "twitter:description"],
  ["meta[name='description']", "name", "description"],
];

function setOrCreateMeta(selector, attr, key, value) {
  if (!value) return null;
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
    created = true;
  }
  const previous = el.getAttribute("content");
  el.setAttribute("content", value);
  return { el, previous, created };
}

export default function useDocumentMeta({
  title,
  description,
  image,
  url,
  type = "article",
  ogTitle,
  ogDescription,
}) {
  useEffect(() => {
    const previousTitle = document.title;
    if (title) document.title = title;

    const fullUrl = url || (typeof window !== "undefined" ? window.location.href : "");
    const ogT = ogTitle || title;
    const ogD = ogDescription || description;
    const valueMap = {
      "og:title": ogT,
      "og:description": ogD,
      "og:type": type,
      "og:url": fullUrl,
      "og:image": image,
      "twitter:card": image ? "summary_large_image" : "summary",
      "twitter:title": ogT,
      "twitter:description": ogD,
      "description": description,
    };

    const restorers = TAGS.map(([selector, attr, key]) =>
      setOrCreateMeta(selector, attr, key, valueMap[key]),
    ).filter(Boolean);

    return () => {
      document.title = previousTitle;
      restorers.forEach(({ el, previous, created }) => {
        if (created) {
          el.parentNode?.removeChild(el);
        } else if (previous !== null) {
          el.setAttribute("content", previous);
        }
      });
    };
  }, [title, description, image, url, type]);
}
